import { z } from 'zod';
import { zStringRequired } from '../../z';
import { refineSchema } from '../refine-schema';
import { registry } from '../registry';
export const encounterTypeOptions = [
    { value: 'office_visit', label: 'Office visit' },
    { value: 'hospital_discharge', label: 'Hospital discharge' },
    { value: 'snf_discharge', label: 'SNF discharge' },
    { value: 'home_visit', label: 'Home visit' },
    { value: 'others', label: 'Others' },
];
export const homeboundReasonOptions = [
    { value: 'homeboundAssistDevice', label: 'Requires assistance to leave home (walker, wheelchair, cane)' },
    { value: 'homeboundAssistPerson', label: 'Requires assistance of another person to leave home' },
    { value: 'homeboundTaxingEffort', label: 'Leaving home requires considerable and taxing effort' },
    { value: 'homeboundMedicalContraindication', label: 'Has medical contraindication to leaving home' },
    { value: 'homeboundCognitiveImpairment', label: 'Cognitive impairment affecting safe community mobility' },
    { value: 'homeboundPsychiatricCondition', label: 'Psychiatric condition affecting ability to leave home' },
];
export const baseFaceToFace = z.object({
    encounterDate: z.string().optional().register(registry, { label: 'F2F Encounter Date' }),
    encounterPerformedBy: z.string().optional().register(registry, { label: 'Encounter performed by: (Physician / Allowed NPP)' }),
    encounterType: z.string().optional().register(registry, { label: 'Encounter type', options: encounterTypeOptions }),
    encounterTypeSpecify: z.string().optional().register(registry, { label: 'Specify encounter type' }),
    certifyDate: zStringRequired().register(registry, {
        label: 'Certify Date of Encounter related to the primary reason the patient requires Home Health Services',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    }),
    medicalConditions: z.string().optional().register(registry, { label: 'List of Medical Conditions' }),
    criteriaAA: z.boolean().optional().register(registry, {
        label: 'Because of illness or injury, the patient needs the aid of supportive devices such as crutches, canes, wheelchairs, and walkers; the use of special transportation; or the assistance of another person in order to leave their place of residence',
    }),
    criteriaAASpecify: z.string().optional().register(registry, { label: 'Specify' }),
    criteriaAB: z.boolean().optional().register(registry, {
        label: 'The patient has a condition such that leaving his or her home is medically contraindicated. Medically restricted due to immunosuppression, infectious illness, risk of infection or injury, or other.',
    }),
    criteriaBA: z.boolean().optional().register(registry, { label: 'There must exist a normal inability to leave the home.' }),
    criteriaBB: z.boolean().optional().register(registry, { label: 'Leaving home requires a considerable and taxing effort.' }),
    homeboundReasons: z.array(z.string()).optional().register(registry, {
        label: 'Patient is homebound due to',
        options: homeboundReasonOptions,
    }),
    skilledNursingCare: z.string().optional().register(registry, { label: 'This Patient Requires Skilled Nursing Care For' }),
    physicalTherapy: z.string().optional().register(registry, { label: 'This Patient Requires Physical Therapy For' }),
    occupationalTherapy: z
        .string()
        .optional()
        .register(registry, { label: 'This Patient Requires Occupational Therapy/Continuing Occupational Therapy For' }),
    speechTherapy: z.string().optional().register(registry, { label: 'This Patient Requires Speech/Language Therapy For' }),
    homeHealthAide: z
        .string()
        .optional()
        .register(registry, { label: 'This Patient Requires Certified Nursing Aide/Home Health Aide For' }),
    medicalSocialServices: z.string().optional().register(registry, { label: 'This Patient Requires Medical Social Services For' }),
    telehealthServices: z.string().optional().register(registry, { label: 'This Patient Requires Telehealth Services For' }),
});
export const faceToFaceValidationSchema = z.object({
    id: z.string(),
    data: refineSchema(baseFaceToFace, (input, ctx) => {
        const payload = input;
        if (payload.criteriaAA && !payload.criteriaAASpecify) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please specify illness or injury',
                path: ['criteriaAASpecify'],
            });
        }
    }),
});
